import React from 'react';

/**
 * Shared helper functions for MapLevel components (MapUnit and MapPlace)
 */

/**
 * Extracts common SVG attributes from attributes object
 * @param {Object} attributes - SVG attributes object
 * @returns {Object} Extracted SVG attributes
 */
export const extractSvgAttributes = (attributes) => {
  const {
    points,
    d,
    x,
    y,
    width,
    height,
    r,
    cx,
    cy,
    transform,
  } = attributes;

  return {
    points,
    d,
    x,
    y,
    width,
    height,
    r,
    cx,
    cy,
    transform,
  };
};

/**
 * Builds a FavoriteButton component for modal headers
 * @param {Object} params - Parameters for building favorite button
 * @param {string} params.type - Entity type ('residence' or 'place')
 * @param {string} params.uuid - Entity UUID
 * @param {Function} params.FavoriteButton - FavoriteButton component
 * @returns {Array} Array with single FavoriteButton component
 */
export const buildFavoriteButtonComponent = ({
  type, uuid, FavoriteButton, forceVisible = false, onToggle = null, ariaLabel = null,
}) => {
  const button = (
    <FavoriteButton
      key={`favorite-${uuid}`}
      type={type}
      uuid={uuid}
      hasShadow={false}
      className="detailHeader__favorite"
      forceVisible={forceVisible}
      onToggle={onToggle}
      ariaLabel={ariaLabel}
    />
  );
  return [button];
};

/**
 * Gets FavoriteButton components array based on whether favorites should show.
 * @param {boolean} showFavorite - Whether to render the favorite button
 * @param {string} type - Entity type ('residence' or 'place')
 * @param {string} uuid - Entity UUID
 * @param {Function} FavoriteButton - FavoriteButton component
 * @param {boolean} forceVisible - Show heart when personalization off (active tgai session)
 * @param {Function} onToggle - Called with (nowFavorited) after a toggle (e.g. tgai capture)
 * @param {string} ariaLabel - Accessible name for the heart (null keeps FavoriteButton's default)
 * @returns {Array} Array of components (empty when favorites should not show)
 */
export const getFavoriteComponents = (
  showFavorite, type, uuid, FavoriteButton, forceVisible = false, onToggle = null, ariaLabel = null,
) => (
  showFavorite ? buildFavoriteButtonComponent({
    type, uuid, FavoriteButton, forceVisible, onToggle, ariaLabel,
  }) : []
);

/**
 * Determines if an element should show heart indicator
 * @param {boolean} personalizationActive - Whether personalization is active
 * @param {string} platform - Platform type
 * @param {boolean} favorited - Whether entity is favorited
 * @returns {boolean} Whether to show heart
 */
export const shouldShowHeart = (personalizationActive, platform, favorited) => (
  (personalizationActive || platform === 'page') && favorited
);

/**
 * Gets a property from an object with a fallback value
 * @param {Object} obj - Object to get property from
 * @param {string} key - Property key
 * @param {*} fallback - Fallback value
 * @returns {*} Property value or fallback
 */
export const getPropertyOrFallback = (obj, key, fallback) => obj?.[key] || fallback;
